import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  AlertTriangle, 
  Clock, 
  FileText, 
  DollarSign, 
  Calendar,
  Users,
  ChevronRight,
  RefreshCw,
  Filter,
  Bell,
  ClipboardList,
  Shield,
  TrendingUp,
  Zap,
  CheckCircle2,
  AlertCircle,
  FileWarning,
  Activity,
  Target,
  Brain,
  Flame
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import api from '@/lib/api';

const severityConfig = {
  CRITICAL: { color: 'bg-red-500 text-white', bgLight: 'bg-red-500/10 border-red-500/30', textColor: 'text-red-500' },
  HIGH: { color: 'bg-orange-500 text-white', bgLight: 'bg-orange-500/10 border-orange-500/30', textColor: 'text-orange-500' },
  MEDIUM: { color: 'bg-yellow-500 text-black', bgLight: 'bg-yellow-500/10 border-yellow-500/30', textColor: 'text-yellow-500' },
  LOW: { color: 'bg-blue-500 text-white', bgLight: 'bg-blue-500/10 border-blue-500/30', textColor: 'text-blue-500' },
};

const typeConfig = {
  EXPIRING_DOCUMENT: { icon: FileText, label: 'Expiring Document', category: 'compliance' },
  PENDING_TIMESHEET: { icon: Clock, label: 'Pending Timesheet', category: 'operations' },
  OPEN_INCIDENT: { icon: AlertTriangle, label: 'Open Incident', category: 'safety' },
  FUNDING_ALERT: { icon: DollarSign, label: 'Funding Alert', category: 'finance' },
  UNASSIGNED_SHIFT: { icon: Calendar, label: 'Unassigned Shift', category: 'operations' },
  MISSING_PROGRESS_NOTE: { icon: ClipboardList, label: 'Missing Progress Note', category: 'compliance' },
  OVERDUE_TASK: { icon: Bell, label: 'Overdue Task', category: 'operations' },
  COMPLIANCE_EXPIRING: { icon: Shield, label: 'Compliance Expiring', category: 'compliance' },
  PLAN_EXPIRING: { icon: Calendar, label: 'Plan Expiring', category: 'compliance' },
  RISK_ALERT: { icon: AlertCircle, label: 'Risk Alert', category: 'safety' },
  BURNOUT_ALERT: { icon: Flame, label: 'Burnout Alert', category: 'workforce' },
};

const ActionDashboard = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [aiAlerts, setAiAlerts] = useState(null);
  const [systemAlerts, setSystemAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('today');

  const fetchActions = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (severityFilter !== 'all') params.append('severity', severityFilter);
      params.append('limit', '100');
      
      // Fetch multiple data sources in parallel
      const [actionRes, aiRes, alertsRes] = await Promise.allSettled([
        api.get(`/action-dashboard?${params}`),
        api.get('/ai-intelligence/dashboard'),
        api.get('/alerts/summary'),
      ]);
      
      if (actionRes.status === 'fulfilled') {
        setItems(actionRes.value.data?.data?.items || []);
        setSummary(actionRes.value.data?.data?.summary || null);
      }
      
      if (aiRes.status === 'fulfilled') {
        setAiAlerts(aiRes.value.data?.data || null);
      }
      
      if (alertsRes.status === 'fulfilled') {
        setSystemAlerts(alertsRes.value.data?.data || null);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to fetch action items');
    } finally {
      setLoading(false);
    }
  }, [severityFilter]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const getTypeInfo = (type) => typeConfig[type] || { icon: AlertTriangle, label: type, category: 'other' };

  const filteredItems = items.filter(item => {
    if (categoryFilter === 'all') return true;
    const info = getTypeInfo(item.type);
    return info.category === categoryFilter;
  });

  const handleItemClick = (item) => {
    switch (item.entity_type) {
      case 'staff_compliance':
        navigate(`/staff/${item.entity_id}?tab=compliance`);   // NAV-1: StaffDetail compliance tab (no /compliance route)
        break;
      case 'timesheet':
        navigate('/timesheets/pending?status=SUBMITTED');   // NAV-1: Timesheet Approval queue (route /roster/timesheets does not exist)
        break;
      case 'incident':
        navigate(`/compliance/incidents/${item.entity_id}`);   // NAV-1: registered incident detail route
        break;
      case 'participant':
        navigate(`/participants/${item.entity_id}`);
        break;
      case 'shift':
        navigate(`/staff/shift/${item.entity_id}`);   // NAV-1: registered shift detail route (no /roster/shifts/:id)
        break;
      case 'task':
        navigate('/staff/tasks');   // NAV-1: tasks list (no /tasks/:id route)
        break;
      default:
        break;
    }
  };

  const criticalCount = summary?.by_severity?.CRITICAL || 0;
  const highCount = summary?.by_severity?.HIGH || 0;
  const totalCount = summary?.total || 0;
  const needsAttention = criticalCount + highCount;

  return (
    <div className="space-y-6" data-testid="action-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Target className="h-7 w-7 text-purple-500" />
            What Needs Attention Today
          </h1>
          <p className="text-muted-foreground mt-1">
            Smart prioritization of urgent actions across your organization
          </p>
        </div>
        <Button 
          onClick={fetchActions} 
          variant="outline"
          disabled={loading}
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Hero Alert Banner */}
      {needsAttention > 0 && (
        <Card className={`border-2 ${criticalCount > 0 ? 'border-red-500 bg-red-500/5' : 'border-orange-500 bg-orange-500/5'}`}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`h-14 w-14 rounded-full flex items-center justify-center ${criticalCount > 0 ? 'bg-red-500/20' : 'bg-orange-500/20'}`}>
                  <AlertCircle className={`h-7 w-7 ${criticalCount > 0 ? 'text-red-500' : 'text-orange-500'}`} />
                </div>
                <div>
                  <h2 className="text-lg font-bold">
                    {needsAttention} item{needsAttention !== 1 ? 's' : ''} need{needsAttention === 1 ? 's' : ''} immediate attention
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {criticalCount > 0 && <span className="text-red-500 font-medium">{criticalCount} critical</span>}
                    {criticalCount > 0 && highCount > 0 && ' • '}
                    {highCount > 0 && <span className="text-orange-500 font-medium">{highCount} high priority</span>}
                  </p>
                </div>
              </div>
              <Button 
                onClick={() => setSeverityFilter('CRITICAL')}
                className={criticalCount > 0 ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-500 hover:bg-orange-600'}
              >
                View Urgent Items
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${severityFilter === 'CRITICAL' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setSeverityFilter(severityFilter === 'CRITICAL' ? 'all' : 'CRITICAL')}
        >
          <CardContent className="py-4 text-center">
            <p className="text-3xl font-bold text-red-500">{criticalCount}</p>
            <p className="text-xs text-muted-foreground">Critical</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${severityFilter === 'HIGH' ? 'ring-2 ring-orange-500' : ''}`}
          onClick={() => setSeverityFilter(severityFilter === 'HIGH' ? 'all' : 'HIGH')}
        >
          <CardContent className="py-4 text-center">
            <p className="text-3xl font-bold text-orange-500">{highCount}</p>
            <p className="text-xs text-muted-foreground">High</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${severityFilter === 'MEDIUM' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setSeverityFilter(severityFilter === 'MEDIUM' ? 'all' : 'MEDIUM')}
        >
          <CardContent className="py-4 text-center">
            <p className="text-3xl font-bold text-yellow-500">{summary?.by_severity?.MEDIUM || 0}</p>
            <p className="text-xs text-muted-foreground">Medium</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${severityFilter === 'LOW' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setSeverityFilter(severityFilter === 'LOW' ? 'all' : 'LOW')}
        >
          <CardContent className="py-4 text-center">
            <p className="text-3xl font-bold text-blue-500">{summary?.by_severity?.LOW || 0}</p>
            <p className="text-xs text-muted-foreground">Low</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer transition-all hover:shadow-md" onClick={() => navigate('/alerts')}>
          <CardContent className="py-4 text-center">
            <p className="text-3xl font-bold text-purple-500">{systemAlerts?.total_active || 0}</p>
            <p className="text-xs text-muted-foreground">System Alerts</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer transition-all hover:shadow-md" onClick={() => navigate('/ai/intelligence')}>
          <CardContent className="py-4 text-center">
            <p className="text-3xl font-bold text-cyan-500">
              {(aiAlerts?.burnout_alerts?.critical_count || 0) + (aiAlerts?.incident_patterns?.critical || 0)}
            </p>
            <p className="text-xs text-muted-foreground">AI Alerts</p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <TabsList className="bg-zinc-100 dark:bg-zinc-900">
            <TabsTrigger value="today" data-testid="tab-today">Action Items</TabsTrigger>
            <TabsTrigger value="expiry" data-testid="tab-expiry">Expiry Warnings</TabsTrigger>
            <TabsTrigger value="risk" data-testid="tab-risk">Risk Alerts</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="tab-compliance">Compliance</TabsTrigger>
          </TabsList>

          {/* Filters */}
          <div className="flex gap-2 items-center">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-36" data-testid="category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="compliance">Compliance</SelectItem>
                <SelectItem value="operations">Operations</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="safety">Safety</SelectItem>
                <SelectItem value="workforce">Workforce</SelectItem>
              </SelectContent>
            </Select>
            {(severityFilter !== 'all' || categoryFilter !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setSeverityFilter('all'); setCategoryFilter('all'); }}
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Today's Actions Tab */}
        <TabsContent value="today" className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredItems.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
                <h3 className="text-lg font-semibold text-green-600">All Clear!</h3>
                <p className="text-muted-foreground">No urgent action items at this time.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredItems.map((item, index) => {
                const typeInfo = getTypeInfo(item.type);
                const TypeIcon = typeInfo.icon;
                const severityStyle = severityConfig[item.severity] || severityConfig.LOW;
                
                return (
                  <Card 
                    key={`${item.type}-${item.entity_id}-${index}`}
                    className={`cursor-pointer transition-all hover:shadow-md border-l-4 ${severityStyle.bgLight}`}
                    onClick={() => handleItemClick(item)}
                    data-testid={`action-item-${index}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2.5 rounded-lg ${severityStyle.bgLight}`}>
                            <TypeIcon className={`w-5 h-5 ${severityStyle.textColor}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium">{item.title}</span>
                              <Badge className={severityStyle.color}>{item.severity}</Badge>
                              <Badge variant="outline" className="text-xs">{typeInfo.label}</Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{item.description}</p>
                            {item.due_date && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                Due: {item.due_date}
                                {item.days_remaining !== undefined && (
                                  <span className={item.days_remaining <= 7 ? 'text-red-500 font-medium' : ''}>
                                    ({item.days_remaining} days)
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Expiry Warnings Tab */}
        <TabsContent value="expiry" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            {/* Document Expiry */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <FileText className="w-5 h-5 text-orange-500" />
                  Document Expiry Warnings
                </CardTitle>
                <CardDescription>Staff documents expiring within 30 days</CardDescription>
              </CardHeader>
              <CardContent>
                {items.filter(i => i.type === 'EXPIRING_DOCUMENT').length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No expiring documents</p>
                ) : (
                  <div className="space-y-3">
                    {items.filter(i => i.type === 'EXPIRING_DOCUMENT').slice(0, 5).map((item, i) => (
                      <div 
                        key={i} 
                        className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900 cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800"
                        onClick={() => handleItemClick(item)}
                      >
                        <div>
                          <p className="font-medium text-sm">{item.title}</p>
                          <p className="text-xs text-muted-foreground">{item.staff_name}</p>
                        </div>
                        <div className="text-right">
                          <Badge className={severityConfig[item.severity]?.color}>{item.days_remaining}d</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Plan Expiry */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar className="w-5 h-5 text-purple-500" />
                  Funding Plan Warnings
                </CardTitle>
                <CardDescription>Participant plans expiring soon</CardDescription>
              </CardHeader>
              <CardContent>
                {items.filter(i => i.type === 'FUNDING_ALERT').length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No funding alerts</p>
                ) : (
                  <div className="space-y-3">
                    {items.filter(i => i.type === 'FUNDING_ALERT').slice(0, 5).map((item, i) => (
                      <div 
                        key={i}
                        className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900 cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800"
                        onClick={() => handleItemClick(item)}
                      >
                        <div>
                          <p className="font-medium text-sm">{item.participant_name}</p>
                          <p className="text-xs text-muted-foreground">{item.utilisation_percent}% utilised</p>
                        </div>
                        <div className="text-right">
                          <Badge className={severityConfig[item.severity]?.color}>{item.severity}</Badge>
                          <p className="text-xs text-muted-foreground mt-1">${item.available_budget?.toLocaleString()} left</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Risk Alerts Tab */}
        <TabsContent value="risk" className="space-y-4">
          <div className="grid lg:grid-cols-2 gap-4">
            {/* AI Risk Patterns */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Brain className="w-5 h-5 text-purple-500" />
                  AI-Detected Risk Patterns
                </CardTitle>
                <CardDescription>Patterns identified by AI analysis</CardDescription>
              </CardHeader>
              <CardContent>
                {!aiAlerts?.incident_patterns?.patterns?.length ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No risk patterns detected</p>
                ) : (
                  <div className="space-y-3">
                    {aiAlerts.incident_patterns.patterns.slice(0, 5).map((pattern, i) => (
                      <div 
                        key={i}
                        className="p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900 cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800"
                        onClick={() => navigate('/ai/intelligence')}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <Badge className={severityConfig[pattern.severity]?.color}>{pattern.severity}</Badge>
                          <span className="text-xs text-muted-foreground">{pattern.pattern_type}</span>
                        </div>
                        <p className="text-sm">{pattern.description}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Burnout Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Flame className="w-5 h-5 text-red-500" />
                  Staff Burnout Alerts
                </CardTitle>
                <CardDescription>Staff at risk of burnout</CardDescription>
              </CardHeader>
              <CardContent>
                {!aiAlerts?.burnout_alerts?.summary?.intervention_needed ? (
                  <p className="text-sm text-muted-foreground text-center py-4">No burnout alerts</p>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
                      <div>
                        <p className="font-medium">Intervention Needed</p>
                        <p className="text-xs text-muted-foreground">Staff requiring immediate attention</p>
                      </div>
                      <span className="text-2xl font-bold text-red-500">{aiAlerts.burnout_alerts.summary.intervention_needed}</span>
                    </div>
                    <Button variant="outline" className="w-full" onClick={() => navigate('/ai/intelligence')}>
                      View Burnout Dashboard
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Open Incidents */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Open Incidents
              </CardTitle>
              <CardDescription>Incidents requiring attention</CardDescription>
            </CardHeader>
            <CardContent>
              {items.filter(i => i.type === 'OPEN_INCIDENT').length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No open incidents</p>
              ) : (
                <div className="space-y-3">
                  {items.filter(i => i.type === 'OPEN_INCIDENT').slice(0, 5).map((item, i) => (
                    <div 
                      key={i}
                      className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900 cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800"
                      onClick={() => handleItemClick(item)}
                    >
                      <div className="flex items-center gap-3">
                        <AlertTriangle className={`w-5 h-5 ${item.ndis_reportable ? 'text-red-500' : 'text-orange-500'}`} />
                        <div>
                          <p className="font-medium text-sm">{item.title}</p>
                          <p className="text-xs text-muted-foreground">{item.incident_number}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.ndis_reportable && <Badge className="bg-red-500 text-white">NDIS</Badge>}
                        <Badge className={severityConfig[item.severity]?.color}>{item.severity}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-4">
          <div className="grid lg:grid-cols-3 gap-4">
            {/* Compliance Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Shield className="w-5 h-5 text-green-500" />
                  Compliance Overview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Expiring Documents</span>
                    <Badge variant="outline">{summary?.by_type?.EXPIRING_DOCUMENT || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Missing Progress Notes</span>
                    <Badge variant="outline">{summary?.by_type?.MISSING_PROGRESS_NOTE || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Pending Timesheets</span>
                    <Badge variant="outline">{summary?.by_type?.PENDING_TIMESHEET || 0}</Badge>
                  </div>
                  <Button variant="outline" className="w-full" onClick={() => navigate('/compliance/document-intelligence')}>
                    Document Intelligence
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Missing Progress Notes */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <ClipboardList className="w-5 h-5 text-amber-500" />
                  Missing Progress Notes
                </CardTitle>
                <CardDescription>Completed shifts without documentation</CardDescription>
              </CardHeader>
              <CardContent>
                {items.filter(i => i.type === 'MISSING_PROGRESS_NOTE').length === 0 ? (
                  <div className="text-center py-6">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-500" />
                    <p className="text-sm text-muted-foreground">All shifts have progress notes</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.filter(i => i.type === 'MISSING_PROGRESS_NOTE').slice(0, 5).map((item, i) => (
                      <div 
                        key={i}
                        className="flex items-center justify-between p-3 rounded-lg bg-zinc-50 dark:bg-zinc-900 cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800"
                        onClick={() => handleItemClick(item)}
                      >
                        <div>
                          <p className="font-medium text-sm">{item.staff_name}</p>
                          <p className="text-xs text-muted-foreground">Shift: {item.date}</p>
                        </div>
                        <Badge className={severityConfig[item.severity]?.color}>{item.severity}</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Quick Actions Footer */}
      <Card className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border-purple-500/30">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-semibold">Quick Actions</h3>
              <p className="text-sm text-muted-foreground">Jump to commonly needed areas</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate('/roster/scheduling-board')}>
                <Calendar className="w-4 h-4 mr-2" />
                Roster
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/compliance/incidents/new')}>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Log Incident
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/alerts')}>
                <Bell className="w-4 h-4 mr-2" />
                All Alerts
              </Button>
              <Button variant="outline" size="sm" onClick={() => navigate('/ai/intelligence')}>
                <Brain className="w-4 h-4 mr-2" />
                AI Intelligence
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ActionDashboard;
